import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import FadeInCard from '../components/FadeInCard'; // Import FadeInCard
import { fetchData } from '../utils/api'; // Import fetchData
import DataIcon from '../components/DataIcon';

/**
 * The Data Page component, displaying a collection of images.
 */
const DataPage = ({ pathname }) => {
  const [data, setData] = useState({});
  const [title, setTitle] = useState();
  const [loadingData, setLoadingData] = useState(true);
  const [errorData, setErrorData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookmarkId, setBookmarkId] = useState(searchParams.get('id') || null);

  useEffect(() => {
    const title = pathname.slice(1);
    document.title = `${process.env.REACT_APP_TITLE} - ${title}`;
    setTitle(title);

    const getData = async () => {
      try {
        setLoadingData(true);
        const data = await fetchData(title);
        setData(data);

        // If there's a bookmarkId, scroll to that element after a short delay
        if (bookmarkId) {
          setTimeout(() => {
            scrollToSection(bookmarkId);
          }, 500); // Delay to ensure content is rendered
        }
      } catch (err) {
        window.location.href = '/not-found';
      } finally {
        setLoadingData(false);
        setSearchParams({});
      }
    };
    
    getData();
  }, [pathname]);

  // Function to handle smooth scrolling to sections
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loadingData) return <div className="text-center text-text-dark text-xl mt-8">Loading data...</div>;
  if (errorData) return <div className="text-center text-red-500 text-xl mt-8">{errorData}</div>;

  return (
    <>
      {data && data.folders && data.folders.length > 0 && (
        <section id="categorySection" className="bg-card-background p-6 md:p-8 rounded-xl shadow-lg mb-8 flex flex-col md:flex-row">
          
          {/* Desktop category sidebar */}
          <nav className="hidden md:block md:w-1/4 lg:w-1/5 p-4 md:border-r border-border-light sticky top-0 self-start">
            <h3 className="text-xl font-bold text-primary-dark mb-4">Categories</h3>
            <ul className="space-y-2">
              {data.folders.map(category => (
                <li key={category.id}>
                  <button
                    onClick={() => scrollToSection(category.id)}
                    className="text-primary-dark hover:text-primary-light font-semibold transition duration-300 text-left w-full"
                  >
                    {category.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="w-full md:w-3/4 lg:w-4/5 md:pl-8 mt-6 md:mt-0">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6 border-b-2 border-border-light pb-2 flex items-center">
              <DataIcon type={title} />
              {title}
            </h2>

            {/* Mobile category menu */}
            <nav className="block md:hidden mb-6 border border-border-light bg-white rounded-lg shadow">
              <h3 className="text-xl font-bold text-primary-dark mb-4 px-4 pt-4">Categories</h3>
              <ul className="flex flex-col gap-2 px-4 pb-4 justify-center">
                {data.folders.map(category => (
                  <li key={category.id}>
                    <button
                      onClick={() => scrollToSection(category.id)}
                      className="w-full text-primary-dark hover:text-primary-light font-semibold transition duration-300 text-center px-3 py-1 border border-border-light rounded-lg hover:bg-background-light"
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {data.folders.map(folder => (
              <div key={folder.id}>
                <h3 id={folder.id} className="text-2xl font-bold text-primary-dark mb-4 mt-6 border-b border-border-light pb-1 pt-5">{folder.name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {folder.images && folder.images.map((img, index) => (
                    <FadeInCard
                      key={index}
                      imageClass={title.toLowerCase().replace(/\s+/g, '-')}
                      imageSrc={img.imageSrc}
                      altText={img.title}
                      title={img.title}
                      description={img.description}
                      selected={bookmarkId === img.title}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data && data.folders && data.folders.length === 0 && (
        <section id="noCategorySection" className="bg-card-background p-6 md:p-8 rounded-xl shadow-lg">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6 border-b-2 border-border-light pb-2 flex items-center">
            <DataIcon type={title} />
            {title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.images.map((img, index) => (
              <FadeInCard
                key={index}
                imageClass={title.toLowerCase().replace(/\s+/g, '-')}
                imageSrc={img.imageSrc}
                altText={img.title}
                title={img.title}
                description={img.description}
                selected={bookmarkId === img.title}
              />
            ))}
          </div>
        </section>
      )}

    </>
  );
};

export default DataPage;
